// What the Renewals lists show (spec C4, C5, C7.3; the Table, Pill, StatCard and SegmentedTabs
// cards). Pure functions, so the rules are tested without a browser.
import type { IncreaseRule, OpenHandoff, RenewalRow, RenewalStatus, SyncInfo } from '../auth/api';
import type { Role } from '../roles';
import { isIncrease, increaseRuleText } from '@safeside/shared';

export { isIncrease, increaseRuleText };

export type Stage = 'open' | 'waiting' | 'withae' | 'closed';
export type TabKey =
  'ask' | 'withme' | 'finished' | 'upcoming' | 'open' | 'waiting' | 'withae' | 'closed';
export type StatKey =
  | 'attention'
  | 'open'
  | 'waiting'
  | 'doneWeek'
  | 'unassigned'
  | 'overdue'
  | 'waitingOnMe'
  | 'overdueOnMe'
  | 'followUps'
  | 'finishedWeek'
  | 'review60'
  | 'review40'
  | 'increaseReview';
export type Page = 'my' | 'all';

/** Statuses from spec C4: label, stage and the kit's pill class. */
export const statuses: Record<RenewalStatus, { label: string; stage: Stage; pill: string }> = {
  pending: { label: 'Pending', stage: 'open', pill: 's-pending' },
  requested: { label: 'Requested', stage: 'waiting', pill: 's-requested' },
  need_payment: { label: 'Need payment', stage: 'waiting', pill: 's-needpay' },
  quote: { label: 'Quote', stage: 'withae', pill: 's-quote' },
  increase: { label: 'Increase', stage: 'withae', pill: 's-increase' },
  non_renewal: { label: 'Non-renewal', stage: 'withae', pill: 's-nrwl' },
  noc: { label: 'NOC', stage: 'withae', pill: 's-noc' },
  done: { label: 'Done', stage: 'closed', pill: 's-done' },
  cancelled: { label: 'Cancelled', stage: 'closed', pill: 's-cancel' },
};

/**
 * The stage a renewal is in: its status's, except With AE once an AE accepted it from someone (a Hand
 * to AE or a reassign, spec C6), whatever status the reason left it (Payment not received keeps
 * Pending). A take over is the AE's own renewal and goes by its status.
 */
export const stageOf = (r: Pick<RenewalRow, 'status' | 'handoff' | 'handledBy'>): Stage => {
  const stage = statuses[r.status].stage;
  return stage !== 'closed' && isWithAe(r) ? 'withae' : stage;
};

/**
 * An AE has it from someone: accepted, or passed on by the AE who accepted it and waiting for the
 * next one (the renewal is no longer with whoever it returns to; Codex on 282).
 */
export const isWithAe = (r: Pick<RenewalRow, 'handoff' | 'handledBy'>) => {
  const h: OpenHandoff | null | undefined = r.handoff;
  if (!h || h.kind === 'take_over') return false;
  return h.state === 'accepted' || (r.handledBy?.id ?? null) !== (h.from?.id ?? null);
};

/** Still waiting for the AE's answer after its Act by day (spec C6: Overdue, red). */
export const isOverdue = (h: OpenHandoff | null | undefined, now: Date) =>
  !!h && h.state === 'waiting' && !!h.actBy && h.actBy < nyToday(now);

/** Waiting for this person's answer (the AE's Needs your answer, spec C6). */
export const isWaitingOn = (r: Pick<RenewalRow, 'handoff'>, meId: string) =>
  r.handoff?.state === 'waiting' && r.handoff.to.id === meId;

/**
 * Where a renewal sits on the AE's My renewals (Yehuda, Nov 1; the Renewals board): a handoff waiting
 * on them (Needs your answer); one they handle and have not finished, unless it waits on another AE
 * they passed it to (With me); else a handoff to them that ended this week (Finished). Null when it
 * is in none of the three.
 */
export type AeTab = 'ask' | 'withme' | 'finished';
export function aeTab(
  r: Pick<RenewalRow, 'handoff' | 'handledBy' | 'status' | 'finishedHandoff'>,
  meId: string,
): AeTab | null {
  if (isWaitingOn(r, meId)) return 'ask';
  const passedOn = r.handoff?.state === 'waiting' && r.handoff.to.id !== meId;
  if (r.handledBy?.id === meId && stageOf(r) !== 'closed' && !passedOn) return 'withme';
  return r.finishedHandoff ? 'finished' : null;
}

/** Urgency bands (spec C5). */
export type Band = 'upcoming' | 'working' | 'alert' | 'urgent' | 'missed';

export function band(daysLeft: number): Band {
  if (daysLeft < 0) return 'missed';
  if (daysLeft <= 14) return 'urgent';
  if (daysLeft <= 30) return 'alert';
  if (daysLeft <= 45) return 'working';
  return 'upcoming';
}

/** The days pill's class: grey 45 to 31 (and upcoming), amber 30 to 15, red 14 or fewer and expired. */
export function daysPill(daysLeft: number): string {
  const b = band(daysLeft);
  if (b === 'alert') return 'pill dpill u-amber';
  if (b === 'urgent' || b === 'missed') return 'pill dpill u-red';
  return 'pill dpill u-grey';
}

/** "22d", and "-2d" once expired. */
export const daysLabel = (daysLeft: number) => `${daysLeft}d`;

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * Why a renewal needs attention today, or null (spec C5): 14 days or fewer left, already expired,
 * Need payment with 10 days or fewer left, Fetch failed twice, or the carrier rejected the portal's
 * login. Closed renewals never do. A Need payment renewal was already sent to the insured, so it
 * waits on the payment rule (10 days) instead of the 14-day one (S14).
 */
export function attentionReason(r: RenewalRow): string | null {
  if (stageOf(r) === 'closed') return null;
  if (r.daysLeft < 0) return `Expired ${plural(-r.daysLeft, 'day', 'days')} ago.`;
  if (r.fetch.attention === 'login_rejected') return 'Login rejected.';
  if (r.fetch.attention === 'failed_twice') return 'Fetch failed twice.';
  if (r.status === 'need_payment') return r.daysLeft <= 10 ? 'Payment not received yet.' : null;
  if (r.daysLeft <= 14) return '14 days or fewer left.';
  return null;
}

export const needsAttention = (r: RenewalRow) => attentionReason(r) !== null;

const month = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
const fullDate = new Intl.DateTimeFormat('en-US', {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});

/** "Nov 12" for a YYYY-MM-DD date (a calendar date, no time zone). */
export const listDate = (ymd: string) => month.format(new Date(`${ymd}T00:00:00Z`));
/** "Thu, Nov 12, 2026". */
export const longDate = (ymd: string) => fullDate.format(new Date(`${ymd}T00:00:00Z`));

/** The days pill tooltip: the date, then the reason when there is one. */
export function expiresTip(r: RenewalRow): string {
  const reason = attentionReason(r);
  if (r.daysLeft < 0) {
    const ago = plural(-r.daysLeft, 'day', 'days');
    return stageOf(r) === 'closed'
      ? `Expired ${listDate(r.expirationDate)}.`
      : `Expired ${listDate(r.expirationDate)}, ${ago} ago, and not closed.`;
  }
  const when = `Expires ${listDate(r.expirationDate)}.`;
  return reason ? `${when} ${reason}` : when;
}

/** The needs-attention line at the top of the Quick view and the record (spec C5), or null. */
export function attentionBanner(r: RenewalRow): string | null {
  const reason = attentionReason(r);
  if (!reason) return null;
  if (r.daysLeft < 0) {
    return `Expired ${longDate(r.expirationDate)}, ${plural(-r.daysLeft, 'day', 'days')} ago, and not closed.`;
  }
  return `Expires ${longDate(r.expirationDate)}. ${reason}`;
}

const dollars = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

/** "$1,996", whole dollars in lists (two decimals only on documents). Empty when unknown. */
export const money = (n: number | null) => (n === null ? '' : dollars.format(n));

/**
 * The Change cell: "14.2%" or "-4.2%", amber for an increase worth flagging (the same rule as the
 * Filters' premium change and Settings, Defaults), green when it goes down.
 */
export function change(r: RenewalRow, rule: IncreaseRule): { text: string; cls: string } | null {
  if (r.priorPremium === null || r.renewalPremium === null || r.priorPremium === 0) return null;
  const pct = ((r.renewalPremium - r.priorPremium) / r.priorPremium) * 100;
  const text = `${pct.toFixed(1)}%`;
  if (isIncrease(rule, r.priorPremium, r.renewalPremium)) return { text, cls: 'c-inc' };
  if (pct < 0) return { text, cls: 'c-down' };
  return { text, cls: 'c-up' };
}

/**
 * The Increase tag (S14, part 1 of the increase review; design Oct 30): at or over the rule, and
 * not already with the AE as the Increase status (its own pill says that already).
 */
export function showIncreaseTag(r: RenewalRow, rule: IncreaseRule): boolean {
  return isIncrease(rule, r.priorPremium, r.renewalPremium) && r.status !== 'increase';
}

/** "+18.2%", the Increase tag's percent (priorPremium and renewalPremium are set; showIncreaseTag checked). */
function increasePercent(r: RenewalRow): string {
  const pct = ((r.renewalPremium! - r.priorPremium!) / r.priorPremium!) * 100;
  return `+${pct.toFixed(1)}%`;
}

/** The Increase tag's text: "Increase +18.2%" (Record header, Quick view header). */
export const increaseTagText = (r: RenewalRow) => `Increase ${increasePercent(r)}`;

/**
 * The Increase tag's tooltip (design Oct 30; wording for Phase 1, S14 part 2): waiting for an AE,
 * who looked it over, or (status already Increase) that it is with the AE. Any AE may answer in
 * Phase 1 (no AE to AMS360 executive link yet), so the waiting line names no one.
 */
export function increaseTooltip(r: RenewalRow, rule: IncreaseRule): string {
  const pct = ((r.renewalPremium! - r.priorPremium!) / r.priorPremium!) * 100;
  const up = r.renewalPremium! - r.priorPremium!;
  const words = `Up ${pct.toFixed(1)}% and +${money(up)}. At or over the ${rule.percent}% rule.`;
  if (r.status === 'increase') return `${words} With the AE as an Increase.`;
  if (r.increaseReview.state === 'looked_over') {
    return `Looked over by ${r.increaseReview.reviewedBy?.name ?? 'someone no longer in the portal'}.`;
  }
  return `${words} Waiting for an AE to look it over.`;
}

/** AMS360's line names, short as on the boards. */
const lineShort: Record<string, string> = {
  Homeowners: 'Home',
  'Dwelling Fire': 'DF',
  Flood: 'Flood',
  'Private Passenger Auto': 'Auto',
  'Umbrella (P)': 'Umbrella',
};
export const shortLine = (line: string) => lineShort[line] ?? line;

export const tabLabels: Record<TabKey, string> = {
  ask: 'Needs your answer',
  withme: 'With me',
  finished: 'Finished',
  upcoming: 'Upcoming',
  open: 'Open',
  waiting: 'Waiting',
  withae: 'With AE',
  closed: 'Closed',
};

/** The role's tabs on each page (spec C4), in order. */
export function tabsFor(page: Page, role: Role): { key: TabKey; label: string }[] {
  if (page === 'all') {
    return [
      { key: 'upcoming', label: 'Upcoming' },
      { key: 'open', label: 'Open' },
      { key: 'waiting', label: 'Waiting' },
      { key: 'withae', label: 'With AE' },
      { key: 'closed', label: 'Closed' },
    ];
  }
  // The AE's My renewals: three tabs only (Yehuda, Nov 1; the SegmentedTabs card).
  if (role === 'Account Executive') {
    return [
      { key: 'ask', label: 'Needs your answer' },
      { key: 'withme', label: 'With me' },
      { key: 'finished', label: 'Finished' },
    ];
  }
  return [
    { key: 'open', label: 'Open' },
    { key: 'waiting', label: 'Waiting' },
    { key: 'closed', label: 'Closed' },
  ];
}

/** The tab a page opens on: the role's first tab, except Upcoming. */
export function defaultTab(page: Page, role: Role): TabKey {
  return page === 'my' && role === 'Account Executive' ? 'ask' : 'open';
}

/** The AE's three My renewals tabs. */
export const isAeTab = (tab: TabKey | null): tab is AeTab =>
  tab === 'ask' || tab === 'withme' || tab === 'finished';

/** The With me tab's Reason: Increase for a reshop, blank for Assign to me (board, Nov 1). */
export const withMeReason = (r: RenewalRow): string =>
  r.increaseReview.state === 'reshop' ? 'Increase' : '';

/** Which tab a renewal belongs to on a page. Upcoming (46 to 60 days) shows only on All renewals. */
export function tabOf(r: RenewalRow, page: Page): TabKey | null {
  const stage = stageOf(r);
  // My renewals has no With AE tab: what the person sent to an AE waits there under Waiting (the
  // Renewals board's VA tabs).
  if (stage === 'withae' && page === 'my') return 'waiting';
  if (stage !== 'open') return stage;
  if (page === 'all' && band(r.daysLeft) === 'upcoming') return 'upcoming';
  return 'open';
}

/** `meId` places the AE's own tabs (Needs your answer, With me, Finished). */
export function inTab(r: RenewalRow, page: Page, tab: TabKey, meId = ''): boolean {
  if (isAeTab(tab)) return page === 'my' && aeTab(r, meId) === tab;
  return tabOf(r, page) === tab;
}

/** The four stat cards per role (spec C7.3, StatCard card). `sub` is a second, muted line. */
export function statsFor(page: Page, role: Role): { key: StatKey; label: string; sub?: string }[] {
  if (role === 'Renewal VA') {
    return [
      { key: 'attention', label: 'Needs attention' },
      { key: 'open', label: 'Open' },
      { key: 'waiting', label: 'Waiting' },
      { key: 'doneWeek', label: 'Done this week' },
    ];
  }
  if (role === 'Account Executive') {
    return page === 'my'
      ? [
          { key: 'waitingOnMe', label: 'Waiting on me', sub: 'handoffs to answer' },
          { key: 'overdueOnMe', label: 'Overdue', sub: 'past Act by' },
          { key: 'followUps', label: 'Follow ups today' },
          { key: 'finishedWeek', label: 'Finished this week' },
        ]
      : [
          { key: 'waitingOnMe', label: 'Waiting on me' },
          { key: 'review60', label: '60-day review' },
          { key: 'review40', label: '40-day review' },
          // All renewals shows the AE the whole department (GET /api/renewals?scope=all, no AE to
          // AMS360 executive link yet; the My accounts scope is still a placeholder).
          { key: 'increaseReview', label: 'Increases to look over', sub: 'in the department' },
        ];
  }
  return [
    { key: 'attention', label: 'Needs attention' },
    { key: 'unassigned', label: 'Unassigned' },
    { key: 'overdue', label: 'Overdue handoffs' },
    { key: 'doneWeek', label: 'Done this week' },
  ];
}

/**
 * Whether a renewal counts for a stat card (and passes its filter). The VA's and the Manager's Done
 * this week comes with a later PR, so that card counts nothing yet. `rule` is only read for
 * `increaseReview`, `meId` for the AE's own cards.
 */
export function inStat(
  r: RenewalRow,
  key: StatKey,
  rule?: IncreaseRule,
  now: Date = new Date(),
  meId = '',
): boolean {
  const stage = stageOf(r);
  switch (key) {
    case 'attention':
      return needsAttention(r);
    case 'open':
      return stage === 'open';
    case 'waiting':
      // The VA's Waiting card counts what their Waiting tab holds, With AE included.
      return stage === 'waiting' || stage === 'withae';
    case 'unassigned':
      return stage !== 'closed' && r.handledBy === null;
    case 'overdue':
      // A handoff not answered after its Act by day (spec C6): Overdue handoffs for the Manager.
      return stage !== 'closed' && isOverdue(r.handoff, now);
    case 'waitingOnMe':
      return isWaitingOn(r, meId);
    case 'overdueOnMe':
      return isWaitingOn(r, meId) && isOverdue(r.handoff, now);
    case 'followUps':
      // A handoff with the AE whose Follow up on day is today (spec C6).
      return aeTab(r, meId) === 'withme' && r.handoff?.followUpOn === nyToday(now);
    case 'finishedWeek':
      // Cancelled handoffs show in Finished but do not count (spec C6).
      return aeTab(r, meId) === 'finished' && r.finishedHandoff?.state !== 'cancelled';
    case 'review60':
      return stage !== 'closed' && r.daysLeft >= 55 && r.daysLeft <= 60;
    case 'review40':
      return stage !== 'closed' && r.daysLeft >= 38 && r.daysLeft <= 42;
    case 'increaseReview':
      return !!rule && needsIncreaseReview(r, rule);
    default:
      return false;
  }
}

/**
 * At or over the increase rule, no answer yet, open and not in the 46 to 60 day Upcoming window
 * (spec approval Oct 30): the AE's "Increases to look over" stat card and the matching Premium
 * change filter option count and filter the same way, from the one function.
 */
export function needsIncreaseReview(r: RenewalRow, rule: IncreaseRule): boolean {
  return (
    stageOf(r) !== 'closed' &&
    band(r.daysLeft) !== 'upcoming' &&
    isIncrease(rule, r.priorPremium, r.renewalPremium) &&
    r.increaseReview.state === 'none'
  );
}

/** The number's color on a stat card: red for attention, amber for overdue, grey at zero. */
export function statClass(key: StatKey, n: number): string {
  if (n === 0) return 'snum n-zero';
  if (key === 'attention' || key === 'waitingOnMe' || key === 'overdueOnMe') return 'snum n-red';
  if (key === 'overdue' || key === 'followUps') return 'snum n-amber';
  if (key === 'doneWeek' || key === 'finishedWeek') return 'snum n-green';
  return 'snum n-ink';
}

export type Group = {
  key: string;
  label: string;
  attention: boolean;
  rows: RenewalRow[];
  meta: string;
};

export const renewalCount = (n: number) => plural(n, 'renewal', 'renewals');

/** "N renewals need attention today" for the alert bar. */
export const attentionLine = (n: number) =>
  n === 1 ? '1 renewal needs attention today' : `${n} renewals need attention today`;

/** The AE's overdue-handoff bar on My renewals (spec C6, the Renewals board). */
export const overdueLine = (n: number) =>
  n === 1 ? '1 handoff is past its Act by date' : `${n} handoffs are past their Act by date`;

export const showing = (shown: number, total: number) => `Showing ${shown} of ${total}`;

const nyDay = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' });

/** Today in New York as 2026-10-26, the format of a date input and of the server's dates. */
export const nyToday = (now: Date) => nyDay.format(now);
const nyTime = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'America/New_York',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

/** "today 02:00", "yesterday 02:00" or "Oct 24 02:00", New York time. */
export function when(iso: string, now: Date): string {
  const at = new Date(iso);
  const day = nyDay.format(at);
  const today = nyDay.format(now);
  // The calendar day before today, so a clock change never skips "yesterday".
  const y = new Date(`${today}T00:00:00Z`);
  y.setUTCDate(y.getUTCDate() - 1);
  const yesterday = y.toISOString().slice(0, 10);
  const time = nyTime.format(at);
  if (day === today) return `today ${time}`;
  if (day === yesterday) return `yesterday ${time}`;
  return `${listDate(day)} ${time}`;
}

/** The table footer's right side (spec C7.3). */
export function syncLine(sync: SyncInfo, now: Date): string {
  if (sync.last?.status === 'running') return 'Syncing from AMS360 now. Read only.';
  if (!sync.lastOkAt) return 'Not synced from AMS360 yet. Read only.';
  return `Synced from AMS360 ${when(sync.lastOkAt, now)}. Read only.`;
}

/** The "Last sync failed" bar (spec C16), or null when the newest run did not fail. */
export function syncFailedLine(sync: SyncInfo, now: Date): string | null {
  if (sync.last?.status !== 'failed') return null;
  const failedAt = nyTime.format(new Date(sync.last.finishedAt ?? sync.last.startedAt));
  const data = sync.lastOkAt ? `Showing data from ${when(sync.lastOkAt, now)}.` : 'No data yet.';
  return `Last sync failed at ${failedAt}. ${data}`;
}

/** The empty line when a page has no renewals at all (spec C7.3). */
export const emptyPage = (role: Role) =>
  role === 'Renewal VA'
    ? "Nothing assigned to you yet. New renewals arrive after tonight's sync."
    : "No open renewals yet. New renewals arrive after tonight's sync.";

/** The empty line for one tab when the page has renewals elsewhere. */
export function emptyTab(tab: TabKey): string {
  switch (tab) {
    case 'open':
      return 'You are all caught up.';
    case 'ask':
      return 'Nothing waiting on you. New handoffs show up here.';
    case 'withme':
      return 'Nothing in progress with you.';
    case 'finished':
      return 'Nothing finished this week.';
    case 'upcoming':
      return 'No renewals are 46 to 60 days out.';
    case 'waiting':
      return 'Nothing is waiting.';
    case 'withae':
      return 'Nothing is with an AE.';
    case 'closed':
      return 'No closed renewals yet.';
  }
}

/** The Renewed in AMS360 tag's tooltip: when the sync first saw it (C3 step 3). */
export const renewedTip = (iso: string, now: Date) =>
  `Renewed in AMS360, seen ${when(iso, now)}. The steps still apply.`;

/** AMS360's encoded department codes, matched from the second probe (docs/decisions.md). */
const departments: Record<string, string> = {
  '!#*': 'Home',
  '!#8': 'Dwelling Fire',
  '!#+': 'Personal Auto',
};
export const departmentName = (code: string | null) =>
  code === null ? 'Not in AMS360' : (departments[code] ?? `Code ${code}`);

/**
 * A tel: link for a phone field, or null when the field is not a plain US number (AMS360 fields
 * can carry a note next to the number; then the text shows without a link).
 */
export function telHref(phone: string): string | null {
  // An extension at the end ("x12", "ext. 12") is fine; any other letters mean a note.
  const number = phone.replace(/\s*(?:ext\.?|x)\s*\d+\s*$/i, '');
  if (/[a-z]/i.test(number)) return null;
  const digits = number.replace(/\D/g, '');
  if (digits.length === 10) return `tel:+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `tel:+${digits}`;
  return null;
}

/** What the Manager hears after pressing Sync now or Try again (spec C7.3, C16). */
export const syncCopy = {
  started: 'Sync started. The list updates when it finishes.',
  running: 'A sync is already running. The list updates when it finishes.',
  notSetUp: 'AMS360 sync is not set up on this server.',
  failedToStart: 'The sync could not start. Try again.',
  finished: 'Synced from AMS360.',
  failed: (ref: string | null) =>
    ref ? `The sync failed. Reference ${ref}.` : 'The sync failed. Try again.',
};

/** Personal Auto (AMS360's line name): the ID cards go with the policy, and no Proof to bank. */
export const isAutoLine = (line: string) => /auto/i.test(line);
